import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Nav from '@src/routes';

export const SAVED_VIEW_SEARCH_PARAM = 'view';

const withViewParam = (search: string, id: string | null): string => {
  const params = new URLSearchParams(search);
  if (id === null) params.delete(SAVED_VIEW_SEARCH_PARAM);
  else params.set(SAVED_VIEW_SEARCH_PARAM, id);
  const next = params.toString();
  return next ? `?${next}` : '';
};

const readViewId = (pathname: string, search: string): string | null =>
  pathname === Nav.Submissions ? new URLSearchParams(search).get(SAVED_VIEW_SEARCH_PARAM) : null;

// The URL is the single source of truth for selection — never mirror it into a store.
export const useSavedViewUrlId = (): string | null => {
  const { pathname, search } = useLocation();
  return readViewId(pathname, search);
};

// Push navigation to the listing carrying only the view param (mirrors the Contracts
// nav link), so back/forward steps between selection states.
export const useSelectSavedView = () => {
  const navigate = useNavigate();
  return useCallback(
    (id: string) => navigate({ pathname: Nav.Submissions, search: withViewParam('', id) }),
    [navigate],
  );
};

const useReplaceViewParamOnListing = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (id: string | null, when: (currentId: string | null) => boolean) => {
      if (pathname !== Nav.Submissions || !when(readViewId(pathname, search))) return;
      navigate({ pathname, search: withViewParam(search, id) }, { replace: true });
    },
    [pathname, search, navigate],
  );
};

export const useActivateSavedViewInUrl = () => {
  const replaceViewParam = useReplaceViewParamOnListing();
  return useCallback((id: string) => replaceViewParam(id, () => true), [replaceViewParam]);
};

// Deselection guarded by id: a selection made while the delete was in flight is kept.
export const useDeselectSavedViewInUrl = () => {
  const replaceViewParam = useReplaceViewParamOnListing();
  return useCallback((id: string) => replaceViewParam(null, (currentId) => currentId === id), [replaceViewParam]);
};
